import React, { useMemo, useCallback, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';
import '../../../personas/calendario/components/LaborFullCalendar.css';
import { CalendarDays, TriangleAlert, Users, Wrench, X, Clock } from 'lucide-react';

// ─── Colour maps ───────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  scheduled: { bg: '#1e3a8a', border: '#3b82f6', text: '#bfdbfe' },
  in_progress: { bg: '#78350f', border: '#f59e0b', text: '#fef08a' },
  completed: { bg: '#14532d', border: '#22c55e', text: '#bbf7d0' },
  cancelled: { bg: '#7f1d1d', border: '#ef4444', text: '#fecaca' },
  pending: { bg: '#1e293b', border: '#94a3b8', text: '#cbd5e1' },
};

const PRIORITY_DOT = {
  low: '🔵',
  medium: '🟡',
  high: '🔴',
  critical: '⚡',
};

const STATUS_LABEL = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
};

function woToEvent(wo) {
  const status = wo.status || 'pending';
  const colors = STATUS_COLORS[status] || STATUS_COLORS.scheduled;
  const priorityDot = PRIORITY_DOT[wo.priority] || '';
  const start = wo.scheduled_date ? new Date(wo.scheduled_date) : undefined;
  const end = (start && wo.estimated_hours)
    ? new Date(start.getTime() + wo.estimated_hours * 60 * 60 * 1000)
    : undefined;

  return {
    id: String(wo.id),
    title: `${priorityDot} ${wo.title || 'Work Order'}`,
    start,
    end,
    allDay: wo.estimated_hours ? false : true,
    backgroundColor: colors.bg,
    borderColor: wo.priority === 'critical' ? '#ef4444' : colors.border,
    textColor: colors.text,
    extendedProps: {
      workOrder: wo,
      status,
    },
  };
}

// ─── Popover component ─────────────────────────────────────────────────────────
function WOPopover({ workOrder, position, onClose, onOpen }) {
  if (!workOrder) return null;

  const statusColors = STATUS_COLORS[workOrder.status] || STATUS_COLORS.scheduled;
  const diffDays = workOrder.scheduled_date
    ? Math.round(
      (new Date(workOrder.scheduled_date).setHours(0, 0, 0, 0) - new Date().setHours(0, 0, 0, 0)) /
      86400000
    )
    : null;

  let diffDaysText = '';
  if (diffDays !== null) {
    if (diffDays === 0) diffDaysText = 'Today';
    else if (diffDays === 1) diffDaysText = 'Tomorrow';
    else if (diffDays > 1) diffDaysText = `In ${diffDays} days`;
    else if (diffDays === -1) diffDaysText = 'Yesterday';
    else diffDaysText = `${Math.abs(diffDays)} days ago`;
  }

  const isOverdue = diffDays < 0 && workOrder.status === 'scheduled';

  const left = Math.min(position.x, window.innerWidth - 330);
  const top = Math.min(position.y, window.innerHeight - 360);

  return (
    <div
      style={{
        position: 'fixed',
        top,
        left,
        zIndex: 9999,
        width: 310,
      }}
    >
      <div className="rounded-xl border border-gray-600 bg-gray-900 shadow-2xl overflow-hidden">
        {/* Header bar */}
        <div
          className="flex items-center justify-between px-3 py-2"
          style={{ background: statusColors.bg, borderBottom: `2px solid ${statusColors.border}` }}
        >
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: statusColors.text }}>
            {STATUS_LABEL[workOrder.status] || workOrder.status}
          </span>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors ml-2 cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-3 space-y-2">
          {/* Title + Priority */}
          <div className="flex items-start gap-2">
            <span className="text-base shrink-0">{PRIORITY_DOT[workOrder.priority] || ''}</span>
            <h3 className="font-bold text-white text-sm leading-snug flex-1">{workOrder.title}</h3>
          </div>

          {/* Description */}
          {workOrder.description && (
            <p className="text-xs text-gray-400 line-clamp-3 leading-relaxed">{workOrder.description}</p>
          )}

          <div className="border-t border-gray-700" />

          {/* Meta rows */}
          <div className="space-y-1.5 text-xs">
            {/* Date */}
            <div className={`flex items-center gap-2 rounded-md px-2 py-1.5 ${isOverdue ? 'bg-red-900/50' : 'bg-gray-800'}`}>
              {isOverdue
                ? <TriangleAlert className="w-4 h-4 text-red-400 shrink-0" />
                : <CalendarDays className="w-4 h-4 text-blue-400 shrink-0" />}
              <span className="text-gray-200">
                {workOrder.scheduled_date
                  ? new Date(workOrder.scheduled_date).toLocaleDateString('en-US', {
                    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
                  })
                  : 'N/A'}
              </span>
              {diffDaysText && (
                <span className={`ml-auto font-semibold ${isOverdue ? 'text-red-300' : 'text-gray-400'}`}>
                  {diffDaysText}
                </span>
              )}
            </div>

            {/* Asset */}
            {workOrder.asset_name && (
              <div className="flex items-center gap-2 bg-gray-800 rounded-md px-2 py-1.5">
                <Wrench className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-gray-200 truncate">{workOrder.asset_name}</span>
              </div>
            )}

            {/* Type */}
            {workOrder.work_order_type && (
              <div className="flex items-center gap-2 bg-gray-800 rounded-md px-2 py-1.5">
                <Clock className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-gray-200 capitalize">{workOrder.work_order_type.replace(/_/g, ' ')}</span>
              </div>
            )}

            {/* Assigned */}
            {workOrder.assignments?.length > 0 && (
              <div className="flex items-center gap-2 bg-gray-800 rounded-md px-2 py-1.5">
                <Users className="w-4 h-4 text-gray-400 shrink-0" />
                <span className="text-gray-200 truncate">
                  {workOrder.assignments.map((a) => a.employee_name).filter(Boolean).join(', ') || 'N/A'}
                </span>
              </div>
            )}
          </div>

          {/* CTA */}
          <button
            onClick={() => { onClose(); onOpen(workOrder); }}
            className="mt-1 w-full text-xs font-semibold py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors cursor-pointer"
          >
            Open Details →
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Legend ────────────────────────────────────────────────────────────────────
function CalendarLegend() {
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1.5 px-3 py-2 bg-gray-800/60 border-t border-l border-r rounded-tr-lg border-gray-700 text-xs">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {Object.entries(STATUS_LABEL).map(([key, label]) => {
          const c = STATUS_COLORS[key];
          return (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ background: c.bg, border: `1.5px solid ${c.border}` }}
              />
              <span className="text-gray-300">{label}</span>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-3 ml-auto border-l border-gray-600 pl-4 text-gray-400">
        {Object.entries(PRIORITY_DOT).map(([k, v]) => (
          <span key={k} className="flex items-center gap-1">{v}<span className="capitalize text-gray-500">{k}</span></span>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export const ExecutionCalendarView = ({ workOrders = [], onWorkOrderClick }) => {
  const [popover, setPopover] = useState(null); // { workOrder, x, y }

  const events = useMemo(
    () => workOrders.filter((wo) => wo.scheduled_date).map(woToEvent),
    [workOrders]
  );

  const calendarKey = useMemo(
    () => events.map((e) => `${e.id}:${e.start}`).join('|'),
    [events]
  );

  const handleEventClick = useCallback((info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    const wo = info.event.extendedProps?.workOrder;
    if (!wo) return;
    const rect = info.el.getBoundingClientRect();
    setPopover({
      workOrder: wo,
      x: rect.left,
      y: rect.bottom + 8,
    });
  }, []);

  const handleEventContent = useCallback((arg) => {
    return (
      <div
        className="px-1 py-0.5 truncate w-full text-[11px] leading-tight font-medium"
        title={arg.event.title}
      >
        {arg.event.title}
      </div>
    );
  }, []);

  return (
    <div className="flex flex-col">
      <CalendarLegend />

      {/* Backdrop to close popover */}
      {popover && (
        <div className="fixed inset-0 z-[9998]" onClick={() => setPopover(null)} />
      )}

      {popover && (
        <WOPopover
          workOrder={popover.workOrder}
          position={{ x: popover.x, y: popover.y }}
          onClose={() => setPopover(null)}
          onOpen={onWorkOrderClick}
        />
      )}

      <div className="labor-fc-wrapper exec-fc-wrapper border border-gray-700 overflow-hidden">
        <FullCalendar
          key={calendarKey}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale={esLocale}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek',
          }}
          height="auto"
          events={events}
          eventDisplay="block"
          dayMaxEvents={4}
          moreLinkClick="popover"
          eventClick={handleEventClick}
          eventContent={handleEventContent}
          eventClassNames={(arg) => {
            const wo = arg.event.extendedProps?.workOrder;
            const classes = ['exec-fc-event'];
            if (wo?.priority) classes.push(`exec-fc-priority-${wo.priority}`);
            return classes;
          }}
        />
      </div>
    </div>
  );
};
